/**
 * The org budgets editor (`manage_budget:write`).
 *
 * Role caps, team caps, member adjustments and model restrictions.
 *
 * ── Where the seed values come from ──
 * `GET /:id/usage` is the single read behind every control here. It returns
 * team caps, per-member resolved `components` (role cap + adjustment), and —
 * since the read-back was wired — `roleCaps` as stored.
 *
 * So role caps prefer their **explicit override** and fall back to the cap
 * observed through a member holding that role; only a role with neither is
 * unobserved, and renders as its default — the SAME default enforcement uses
 * (`DEFAULT_ROLE_CAPS` in `@papan/types`, #767; it was `null` here, which
 * showed guests as unlimited while they were enforced at 0). That fall-back
 * lives in `logic.ts` so it can be tested without a DOM.
 *
 * This paragraph used to say role caps were derived only from members. That
 * was true before the read-back landed and is the reason `logic.ts` exists;
 * leaving it here described an editor that silently reset a field the admin
 * had set, which is the opposite of what the code now does.
 *
 * ── The save model ──
 * Every control here STAGES and commits on an explicit Save, including the
 * model grid below (which always did). One sticky bar covers the whole page.
 * Caps committed on blur between the 2026-08-10 integration merge and
 * 2026-08-12; `settings.budget.editor.desc` describes the current behaviour and
 * must be changed in the same commit as any change to it — it is the only thing
 * on screen telling an admin what happens to a money ceiling, and no test can
 * catch it going stale, because every test here exercises the logic the
 * sentence describes rather than the sentence. It has been wrong in both
 * directions already.
 *
 * ── One control this screen renders, one it deliberately does not ──
 *
 * This paragraph described BOTH as absent until 2026-08-12, while the code
 * below already rendered one of them. Two of them, in sequence, are exactly the
 * "a sentence can outlive the behaviour it describes" failure the note above
 * warns about — so state the current split rather than the history.
 *
 * **Alert thresholds: HERE, restored 2026-08-12 (M2).** They were removed on
 * 2026-08-07, which left alerts firing at a hardcoded 80/100% nobody had
 * chosen, while `OrgUsageResponse.thresholds` was fetched on every load and
 * read by nothing. Restoring cost more than the JSX: the 28
 * `settings.budget.thresholds.*` keys had been deleted from all four locales
 * and had to be re-authored. See the section's own note for why it stages
 * behind its OWN Save rather than the page bar — it writes one object where
 * the caps write N rows, and a whole-object PUT inside a partial-failure loop
 * would report "3 of 5 saved" about something that cannot half-save.
 *
 * **Enforcement mode (`enforce` / `warn_only`): NOT here, and that is a
 * security statement rather than a gap.** `warn_only` makes every one of this
 * org's internal caps advisory, so a toggle on this page puts that one click
 * from anyone holding `manage_budget:write` — the very grant the mode is meant
 * to constrain. It is set from the PLATFORM console instead
 * (`PUT /api/admin/tenants/:tenantId/enforcement-mode`, `customers:write`),
 * which is a different grant and a different blast radius, and it is staged
 * there beside the ceilings because it is the second half of one decision: the
 * ceiling is the number, the mode is what the number DOES when it is reached.
 *
 * Both dashboards keep showing the mode read-only via `EnforcementNote`, which
 * is now "here is the policy, your platform operator sets it" rather than the
 * dead end it was while no surface could change it at all.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast, useMyCapabilities, useRefreshWhenShown, useBillingCurrency, useDraftState } from '@papan/core-ui/ui'
import { CAP_ROLES, type BudgetCap } from '@papan/types'
import { budgetApi, type OrgUsageResponse } from '../../client/budget'
import { pinnedBatch } from '@papan/api-client'
import { CapInput } from '@papan/core-ui/ui'
import { seedRoleCaps, seedThresholds, capKey, parseCapKey, stagedCap, dirtyCapKeys, type CapDraft } from './logic'
import { useMoney, MoneyText, Section, ApproximateFootnote } from './bits'
import { ModelRestrictionsEditor, type ModelRestrictionsHandle } from './ModelRestrictionsEditor'
import { TeamCapsTable } from './TeamCapsTable'

export function BudgetsEditor({
  tenantId,
  userEmail,
  onNavigate,
}: {
  tenantId?: string
  userEmail?: string
  /** Settings-pane navigation, threaded from the app shell. Optional so the
   *  component still renders anywhere the shell doesn't provide it. */
  /** Jump to the budget dashboard. The destination route is owned by the
   *  caller (App.tsx / MobileShell), so this takes no page argument. */
  onNavigate?: () => void
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  // `capsLoading` is NOT the local `loading` below: that one is this page's own
  // data, this one is whether the caller's GRANT has resolved. `can()` returns
  // false for both "denied" and "still fetching", so without it the refusal
  // copy paints first and an admin is told they lack a permission they hold —
  // the same trap `SpendingPage` spells out, and observed here on a cold load
  // whose capabilities request took 5s.
  const { can, loading: capsLoading } = useMyCapabilities(tenantId, false, userEmail)
  const canWrite = can('manage_budget', 'write')
  const canRead = can('manage_budget', 'read')
  /** What the DEPLOYMENT charges in — the unit every cap on this page is in. */
  const billingCurrency = useBillingCurrency()
  const [data, setData] = useState<OrgUsageResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    if (!tenantId) return
    setLoading(true)
    try {
      setData(await budgetApi.getOrgUsage(tenantId))
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load budgets')
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  // Loads for any READER, not just a writer (L8). The page used to refuse a
  // read-only caller outright — and a `manage_budget:read` holder is entitled
  // to see every cap on it, so hiding the values was a downgrade justified by
  // nothing. They now get the page with the reason stated once and every
  // control disabled with its value visible, which is what the platform
  // console does for the same situation.
  useEffect(() => { if (canRead) load(); else setLoading(false) }, [canRead, load])

  const money = useMoney()


  // Role caps: override > a member's enforced value > the role's default, read
  // from the ONE table enforcement uses. See `seedRoleCaps` (#767).
  const roleCaps = useMemo(() => seedRoleCaps(data), [data])

  /**
   * ── Staged cap edits (A6, restored) ───────────────────────────────────
   *
   * Every cap field used to write the moment it lost focus. These are money
   * ceilings: a mistyped digit plus a stray click set one, and the only signal
   * was a toast that had already gone. The platform console stages for exactly
   * this reason, and the model grid on THIS page already did — so the page
   * disagreed with itself about what a control does when you look away.
   *
   * `serverCapOf` is what "dirty" is measured against, re-read from the CURRENT
   * server state on every render rather than from a snapshot taken when staging
   * began. That makes two things true for free: typing a value and typing it
   * back leaves nothing dirty, and a row that landed in a partially-failed save
   * stops showing as unsaved without any bookkeeping.
   */
  // #897 — staged caps, kept across a phone/desktop layout swap.
  const draftBase = tenantId ? `budget:${tenantId}` : null
  const [draft, setDraft, { clear: clearDraft }] = useDraftState<CapDraft>(draftBase && `${draftBase}:caps`, {})
  const serverCapOf = useCallback((key: string): BudgetCap | undefined => {
    const parsed = parseCapKey(key)
    if (!parsed || !data) return undefined
    const { scope, id } = parsed
    if (scope === 'role') return roleCaps[id]?.cap ?? null
    if (scope === 'team') return data.teams.find(x => x.id === id)?.cap
    return data.users.find(u => u.email === id)?.caps.components.adjust
  }, [data, roleCaps])
  const dirtyKeys = dirtyCapKeys(draft, serverCapOf)

  /** Staged model-grid subjects, reported up by the child so ONE bar can
   *  speak for the whole page (decision 5). */
  const [modelDirty, setModelDirty] = useState(0)

  /**
   * Alert thresholds (M2), restored.
   *
   * The route, the client method, `seedThresholds` and `DEFAULT_THRESHOLD_PCT`
   * were all still live and tested — only the controls and their 28 strings
   * were deleted (2026-08-07). So an admin could not choose WHEN they are
   * warned, and alerts fired at a hardcoded 80/100% nobody had picked, while
   * `OrgUsageResponse.thresholds` was fetched on every load and read by
   * nothing.
   *
   * Staged behind its own Save rather than the page bar: this writes ONE
   * endpoint with a whole object, where the caps write N endpoints row by row.
   * Folding a whole-object PUT into a partial-failure loop would mean a
   * "3 of 5 saved" report covering something that cannot half-save.
   */
  // Drafts over the server read-back (#897, kept across a layout swap): the
  // fields show what is stored until edited, and an edit is never overwritten
  // by a `load()` — a cap save refetches while an alert edit is in progress.
  const seededThresholds = useMemo(() => (data ? seedThresholds(data.thresholds) : null), [data])
  const [pct, setPct, { settle: settlePct }] = useDraftState<number[]>(draftBase && `${draftBase}:alertPct`, () => seededThresholds?.pct ?? [])
  const [absText, setAbsText, { settle: settleAbs }] = useDraftState<string>(draftBase && `${draftBase}:alertAbs`, () => seededThresholds?.absText ?? '')
  const models = useRef<ModelRestrictionsHandle>(null)
  const pending = dirtyKeys.length + modelDirty

  /**
   * Re-fetch when the deck comes back on screen — but NOT while edits are staged.
   *
   * The desktop shell keeps a deck MOUNTED behind `display: none`, so the load
   * effect above would otherwise run exactly once per browser tab, and this
   * page would show ceilings as they were when Settings was first opened. That
   * matters more here than on a read-only screen: a stale value is also the
   * value an admin is about to overwrite.
   *
   * The dirty guard is the same call `CustomerDetail` makes. Nothing here
   * clears the draft on reload, so staged edits would survive — but `dirtyKeys`
   * is measured against the CURRENT server value, so a refresh under the
   * admin's fingers can silently un-dirty a row they still mean to save.
   * Withholding the refresh while anything is pending keeps Save meaning what
   * the screen showed when they started typing.
   */
  useRefreshWhenShown(() => { if (canRead && pending === 0) void load() })

  /**
   * Commit everything staged, in one action, and report once.
   *
   * COLLECT-AND-CONTINUE on both halves, which is a deliberate convergence.
   * The cap loop used to stop at the first failure; the model loop already
   * collected failures and carried on, with a comment recording why stopping
   * was a bug — one rejected subject "silently destroyed edits the admin had
   * made to every other row and never sent". That argument does not depend on
   * which table the row is in, and with one Save button owning both loops the
   * two policies could not have stayed different anyway.
   *
   * Nothing typed is discarded on failure. Only the keys that ACTUALLY landed
   * leave the draft — a blanket `setDraft({})` after a partial save throws away
   * the very edits the toast is reporting as failed, which is the trap
   * `ModelRestrictionsEditor` documents in its own loop. `dirtyKeys` then
   * re-measures against reloaded server truth, so what landed stops showing as
   * unsaved and what did not stays staged and visible.
   *
   * (The first version of this cleared the whole draft while this comment
   * claimed otherwise. Caught reviewing the diff, not by a test — the failure
   * needs a rejecting endpoint to reproduce.)
   */
  const saveAll = async () => {
    if (!tenantId || pending === 0) return
    setBusy(true)
    // #1316 review round 2 (owner, 2026-10-28): the Save the person pressed FINISHES as the identity that pressed it —
    // every row is written (and audited) as it, never as the next session; nothing is shown once that session ended
    const batch = pinnedBatch()
    const failed: string[] = []
    /** Keys that actually landed. Only these leave the draft. */
    const savedKeys: string[] = []
    try {
      for (const key of dirtyKeys) {
        const parsed = parseCapKey(key)
        if (!parsed) continue
        const { scope, id } = parsed
        const capBillingMicro = draft[key]
        try {
          if (scope === 'role') await batch.run(() => budgetApi.setRoleBudget(tenantId, id, { capBillingMicro }))
          else if (scope === 'team') await batch.run(() => budgetApi.setTeamBudget(tenantId, id, { capBillingMicro }))
          else await batch.run(() => budgetApi.setMemberBudget(tenantId, id, { capBillingMicro }))
          savedKeys.push(key)
        } catch (e: any) {
          // NAMED. "Team not found" on its own leaves an admin hunting which
          // of a dozen rows it meant.
          failed.push(`${id}: ${e?.message || 'failed'}`)
        }
      }
      // The grid's own save, through the ref. It reports rather than toasts, so
      // the counts below cover the whole page in one sentence.
      const m = await models.current?.save(batch)
      const saved = savedKeys.length + (m?.saved ?? 0)
      failed.push(...(m?.failed ?? []))

      // ONLY the keys that landed. `setDraft({})` here would discard the ones
      // that did NOT — losing an admin's typed ceiling with a toast naming it
      // as the failure, which is the same trap `ModelRestrictionsEditor`
      // documents: a rejected row silently destroying edits that were never
      // sent. Reloading alone does not save us either, because `load()` brings
      // back server truth for rows the draft no longer covers.
      setDraft(d => {
        const next = { ...d }
        for (const k of savedKeys) delete next[k]
        return next
      })
      if (batch.ended()) return
      await load()

      if (failed.length) {
        toast.error(t('settings.budget.caps.savePartial', {
          saved, total: saved + failed.length, error: failed.join('; '),
        }))
      } else {
        toast.success(saved === 1
          ? t('settings.budget.caps.saveOne')
          : t('settings.budget.caps.saveAll', { count: saved }))
      }
    } finally {
      setBusy(false)
    }
  }

  /**
   * Validated CLIENT-side for the message, not as the guard — the route
   * re-validates. Saying which field is wrong beats a generic rejection after
   * a round trip.
   *
   * An empty `pct` array is a REAL state ("no percentage alerts") and must
   * survive as `[]`, never fall back to the defaults — that distinction is
   * exactly what `seedThresholds` exists to preserve on the way in.
   */
  const saveThresholds = async () => {
    if (!tenantId) return
    if (pct.some(n => !Number.isInteger(n) || n < 1 || n > 100)) {
      toast.error(t('settings.budget.thresholds.invalidPct'))
      return
    }
    const absUsd = absText.split(',').map(x => x.trim()).filter(Boolean).map(Number)
    if (absUsd.some(n => !Number.isFinite(n) || n <= 0)) {
      toast.error(t('settings.budget.thresholds.invalidAbs'))
      return
    }
    setBusy(true)
    try {
      // Billing-currency MICRO on the wire, MAJOR units in the field — the
      // inverse of what `seedThresholds` does on the way in. A micro is 10^-6 of
      // the major unit in every currency, so this factor is not currency-aware
      // and must not be made so (a MINOR unit would be: 100 per major in MYR,
      // 1 in JPY, 1000 in BHD).
      const sentMicro = absUsd.map(d => Math.round(d * 1_000_000))
      await budgetApi.setAlertThresholds(tenantId, { pct, absUsd: sentMicro })
      toast.success(t('settings.budget.thresholds.saved'))
      // Settled, not cleared: they stay until the reload shows the stored
      // thresholds (no flash of the old ones; kept on screen if it fails).
      // The amounts settle against the text the RELOAD will render for what
      // was sent ("10,20" is stored as [10,20] and shows as "10, 20"), or a
      // retyped-but-equal list would never match and stick.
      settlePct()
      settleAbs(seedThresholds({ pct, absUsd: sentMicro }).absText)
      await load()
    } catch (e: any) {
      toast.error(e?.message || 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  const discardAll = () => {
    clearDraft()
    models.current?.discard()
  }

  if (!tenantId) return <div className="p-4 themed-text-muted text-sm">{t('settings.budget.noTenant')}</div>
  // Refuse only once the grant is RESOLVED — see `capsLoading` above.
  if (capsLoading) return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
  // Only a caller with NEITHER half is refused. `access` is an ordered set and
  // write implies read, so this is the genuine no-access case.
  if (!canRead) return <div className="p-4 themed-text-muted text-sm">{t('settings.budget.noPermissionRead')}</div>
  // First load only: once data is in hand, every on-blur/on-toggle save calls
  // `load()` again, which flips `loading` back on. Tearing the whole editor down
  // to a spinner on each save unmounts the form and resets the scroll position
  // (bad UX when tweaking a field mid-page). Inputs are already disabled via
  // `busy` during a save, so keep the form mounted and let the reload happen
  // underneath it — matching PlatformBudgetAdmin's first-load-only guard.
  if (!data) {
    if (loading) return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
    return <div className="p-4 text-sm text-red-400">{error}</div>
  }

  // Cap inputs are typed and stored in the BILLING currency — the write
  // endpoints take billing-currency micro with no conversion, and the ceilings
  // they set are compared against a `cost_billing_micro` sum.
  //
  // The label was `'USD'` while the caps were USD, which was correct then and
  // is a mislabel now: an admin typing "200" under a "$" prefix on an MYR
  // deployment is setting RM200. It is deliberately NOT the DISPLAY currency —
  // that is a render-time multiplier for reading spend, and labelling an input
  // with it is the original bug this constant was introduced to fix.
  const cc = billingCurrency

  /**
   * The typed USD amount, shown converted beneath the field (D7).
   *
   * The USD label above is correct and was not the problem. The problem was
   * that it is the ONLY currency on this screen, while every figure on the
   * Spending dashboard the admin just came from is in the tenant's display
   * currency — so "cap them at about what they spent" needed the credit rate
   * done in the reader's head.
   *
   * `undefined` when the tenant displays in USD: `money.approximate` is false
   * there, the two numbers are identical, and a line restating the field is
   * noise. Passing `money.fmt` (not a rate) keeps the resolution chain in
   * `useMoney`, which is what the `no-raw-display-rate-reads` guard is for.
   */
  const capHint = money.approximate ? money.fmt : undefined

  return (
    <div className="max-w-4xl">
      <div className="flex items-start justify-between gap-4">
        <h2 className="themed-text text-lg font-medium mb-1">{t('settings.budget.editor.title')}</h2>
        {canRead && onNavigate && (
          <button
            onClick={() => onNavigate()}
            className="text-xs themed-text-muted hover:themed-text shrink-0 transition-colors"
          >
            {t('settings.budget.editor.viewDashboard')} →
          </button>
        )}
      </div>
      {/* "Changes save when you leave a field" was true and is now false — see
          the locale change in the same commit. A sentence describing what
          happens to a money ceiling is the one piece of copy that must not
          outlive the behaviour it describes, and no test can catch it: every
          test here exercises the logic the sentence describes, not the
          sentence. It was wrong in the opposite direction once before. */}
      <p className="themed-text-muted text-sm mb-1">{t('settings.budget.editor.desc')}</p>
      <p className="themed-text-muted text-xs mb-4">
        <span className="text-blue-300">{t('settings.budget.unlimited')}</span> = {t('settings.budget.editor.blankHint')}
        {' · '}
        <span className="text-red-300">{t('settings.budget.blocked')}</span> = {t('settings.budget.editor.zeroHint')}
      </p>

      {/* Stated once, at the top, rather than by hiding the page or
          redirecting away — the URL stays truthful and the reader learns why
          (L8). `settings.budget.readOnly` has been in all four locales since
          A4 shipped this and the unwind lost it. */}
      {!canWrite && (
        <div className="rounded-lg border border-blue-500/40 bg-blue-500/10 px-3 py-2 mb-4">
          <p className="themed-text-secondary text-xs">{t('settings.budget.readOnly')}</p>
        </div>
      )}

      {/* ── Role caps ─────────────────────────────────────────────── */}
      <Section title={t('settings.budget.roleCaps.title')} desc={t('settings.budget.roleCaps.desc')}>
        <div className="themed-bg-secondary rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b themed-border">
                <th className="text-left px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.role')}</th>
                <th className="text-left px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.cap')}</th>
              </tr>
            </thead>
            <tbody>
              {CAP_ROLES.map(r => {
                const cur = roleCaps[r.id]
                return (
                  <tr key={r.id} className="border-b themed-border last:border-b-0">
                    <td className="px-4 py-2.5">
                      <div className="themed-text">{r.label}</div>
                      {!cur.observed && (
                        <div className="text-[10px] themed-text-muted">{t('settings.budget.roleCaps.unobserved')}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {/* A role cap is an additive CONTRIBUTION, exactly like a
                          team cap — `pooledCap` sums personal + team + role and
                          nothing special-cases the role component. So 0 here
                          "adds nothing"; it does not block, and a member of a
                          funded team keeps spending. Rendering it as the red
                          "Blocked" chip stated the opposite of what enforcement
                          does, and stated it two inches from the team column
                          that says "0 contributes nothing" about the identical
                          arithmetic — the same value reading two ways on one
                          screen. The genuine block is the RESOLVED pooled
                          ceiling, which the dashboard's Allowance column already
                          shows as "Blocked" when every component is 0. */}
                      <CapInput
                        value={stagedCap(draft, capKey('role', r.id), cur.cap)}
                        draftKey={draftBase ? `${draftBase}:typed:${capKey('role', r.id)}` : undefined}
                        currencyCode={cc}
                        displayHint={capHint}
                        zeroIsBlock={false}
                        disabled={busy || !canWrite}
                        ariaLabel={`${r.label} — ${t('settings.budget.col.cap')}`}
                        onCommit={v => setDraft(d => ({ ...d, [capKey('role', r.id)]: v }))}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {/* ── Team caps ─────────────────────────────────────────────── */}
      <Section title={t('settings.budget.teamCaps.title')} desc={t('settings.budget.teamCaps.desc')}>
        {/* A tree, not a flat list (#770): contributions stack down the
            ancestry. Staged edits stay in THIS component's draft, so a row
            collapsed out of sight is still committed by the page Save. */}
        <TeamCapsTable
          teams={data.teams}
          draft={draft}
          dirtyKeys={dirtyKeys}
          onStage={(key, v) => setDraft(d => ({ ...d, [key]: v }))}
          draftKeyBase={draftBase ? `${draftBase}:typed` : undefined}
          disabled={busy || !canWrite}
          currencyCode={cc}
          displayHint={capHint}
          money={money}
        />
      </Section>

      {/* ── Member adjustments ────────────────────────────────────── */}
      <Section title={t('settings.budget.memberAdjust.title')} desc={t('settings.budget.memberAdjust.desc')}>
        <div className="themed-bg-secondary rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b themed-border">
                <th className="text-left px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.member')}</th>
                <th className="text-left px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.adjust')}</th>
                <th className="text-left px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.effectiveCap')}</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map(u => (
                <tr key={u.email} className="border-b themed-border last:border-b-0">
                  <td className="px-4 py-2.5 themed-text-muted font-mono text-xs">{u.email}</td>
                  {/* Adjustments are signed: a negative one is the documented
                      "zero this member out" pattern, so negatives are allowed
                      here and nowhere else. */}
                  <td className="px-4 py-2.5">
                    <CapInput
                      value={stagedCap(draft, capKey('member', u.email), u.caps.components.adjust)}
                      draftKey={draftBase ? `${draftBase}:typed:${capKey('member', u.email)}` : undefined}
                      currencyCode={cc}
                      displayHint={capHint}
                      allowNegative
                      zeroIsBlock={false}
                      nullLabel={t('settings.budget.noAdjustment')}
                      disabled={busy || !canWrite}
                      ariaLabel={`${u.email} — ${t('settings.budget.col.adjust')}`}
                      onCommit={v => setDraft(d => ({ ...d, [capKey('member', u.email)]: v }))}
                    />
                  </td>
                  {/* The single resolved per-user cap (role + Σ team + adjustment). */}
                  <td className="px-4 py-2.5 text-xs whitespace-nowrap">
                    {u.caps.userCap === null
                      ? <span className="text-blue-300">{t('settings.budget.unlimited')}</span>
                      : <MoneyText micro={u.caps.userCap} money={money} className="themed-text" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* ── Budget alerts (M2) ──────────────────────────────────────── */}
      <Section title={t('settings.budget.thresholds.title')} desc={t('settings.budget.thresholds.desc')}>
        <div className="themed-bg-secondary rounded-lg px-4 py-3 space-y-3">
          <label className="block">
            <span className="block text-[11px] themed-text-muted uppercase tracking-wide mb-1">
              {t('settings.budget.thresholds.pctLabel')}
            </span>
            <input
              value={pct.join(', ')}
              disabled={busy || !canWrite}
              onChange={e => setPct(
                e.target.value.split(',').map(x => Number(x.trim())).filter(n => Number.isFinite(n)),
              )}
              placeholder={t('settings.budget.thresholds.none')}
              className="themed-input border rounded px-2 py-1 text-xs w-56 outline-none focus:border-blue-500 disabled:opacity-50"
            />
          </label>
          <label className="block">
            <span className="block text-[11px] themed-text-muted uppercase tracking-wide mb-1">
              {t('settings.budget.thresholds.absLabel')}
            </span>
            <input
              value={absText}
              disabled={busy || !canWrite}
              onChange={e => setAbsText(e.target.value)}
              className="themed-input border rounded px-2 py-1 text-xs w-56 outline-none focus:border-blue-500 disabled:opacity-50"
            />
          </label>
          {canWrite && (
            <button
              onClick={() => void saveThresholds()}
              disabled={busy}
              className="px-3 py-1.5 text-xs rounded themed-btn-secondary disabled:opacity-50 font-medium"
            >
              {t('settings.budget.thresholds.save')}
            </button>
          )}
        </div>
      </Section>

      {/* Model access sits with the spend controls, not in a permissions
          screen: restricting a member to cheaper models is a budget lever, and
          the audit trail lands in the same budget_audit log. */}
      <ModelRestrictionsEditor
        ref={models}
        tenantId={tenantId}
        canWrite={canWrite}
        members={data.users}
        teams={data.teams}
        onDirtyChange={setModelDirty}
      />

      <ApproximateFootnote money={money} />

      {/* ── The page's ONE Save bar ──────────────────────────────────────
          Sticky, unlike the console's equivalent: this page is long (three cap
          tables and a model grid the width of the catalog), so a bar pinned at
          the bottom of the document would put the only Save somewhere below the
          fold on every screen that matters.

          It counts BOTH kinds of staged change. The grid used to carry its own
          inline Save with its own count, borrowed from the pricing admin's
          copy — two bars for one page, and "Save" meaning "save the half you
          are looking at". */}
      {pending > 0 && (
        <div
          data-testid="budget-save-bar"
          // z-40: above the model grid's sticky header and column (z-30 / z-20 / z-10,
          // ModelRestrictionsEditor) and the team table's (z-10) — without a z-index
          // they slid OVER this bar when scrolled under it, hiding Save.
          className="sticky bottom-3 z-40 mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-blue-500/40 bg-blue-500/10 backdrop-blur px-3 py-2"
        >
          <span className="themed-text text-xs font-medium">
            {[
              dirtyKeys.length > 0 ? t('settings.budget.caps.unsaved', { count: dirtyKeys.length }) : null,
              modelDirty > 0 ? t('settings.budget.caps.unsavedModels', { count: modelDirty }) : null,
            ].filter(Boolean).join(' · ')}
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={discardAll}
              disabled={busy || !canWrite}
              className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50 transition-colors"
            >
              {t('settings.budget.caps.discard')}
            </button>
            <button
              onClick={() => void saveAll()}
              disabled={busy || !canWrite}
              className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50 font-medium transition-colors"
            >
              {busy ? t('common:saving') : t('common:save')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
